import React, { useCallback, useEffect } from 'react';
import { Device } from '@twilio/voice-sdk';
import axios from 'axios';
import { isEmpty, isNil } from 'lodash';

import { ClaimContextProvider } from '~/components/ClaimContainer';
import mixpanel from '~/components/CmsMain/mixpanel';
import { TWILIO_ERRORS_TO_IGNORE } from '~/components/communications/PhoneCall/components/CmsCallCenter';
import { attachTokenRefreshHandler, TOKEN_REFRESH_MS } from '~/components/communications/PhoneCall/twilioDeviceToken';
import { MinimizedDialogContextProvider } from '~/components/core/MinimizedBar/Context';
import { useCms } from '~/components/hooks/useCms';
import LoadingDialog from '~/components/LoadingDialog';
import { useFetchClaim } from '~/hooks/useFetchClaim';
import { MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import { reportAxiosError, reportTwilioError } from '~/Utils';

import CallInProgressCard from './components/CallInProgressCard';
import { CommunicationCallProvider, useCommunicationCallContext } from './Context/CommunicationCallContext';

const IncomingCallInProgressContainer = () => (
  <CommunicationCallProvider>
    <MinimizedDialogContextProvider>
      <IncomingCallInProgressContainerInner />
    </MinimizedDialogContextProvider>
  </CommunicationCallProvider>
);

export const IncomingCallInProgressContainerInner = () => {
  const deviceRef = React.useRef(undefined);
  // deviceRef is set only once the accept call returns, so it cannot stop the effect re-running
  // while the device registers; a second run would accept the same reservation again and fail.
  const hasStartedCallSetupRef = React.useRef(false);
  const offlineErrorRef = React.useRef(false);
  const { callInProgress, setCallInProgress, userReloadTwilioDetails } = useCms();
  const [errorStartingCall, setErrorStartingCall] = React.useState(false);
  const { callSid, currCallSid, communicationId, claimToAttachId } = callInProgress;
  const [claim, isLoadingClaim, isErrorLoadingClaim, reloadClaim] = useFetchClaim(claimToAttachId);

  const { callStatus, communication, updateCallConnection, updateDisconnectOrCancel, updateCommunication } =
    useCommunicationCallContext();

  const fetchAccessToken = async () => {
    const res = await axios.get('/api/v1/calls/twilio_access_token_accept');
    const token = res.data;
    return token;
  };

  const handleCallAccepted = useCallback(
    (call) => {
      updateCallConnection(call);
      mixpanel.track(MIXPANEL_EVENTS.INCOMING_PHONE_CALL_COMMUNICATION_ACCEPTED, { communicationId });
    },
    [communicationId, updateCallConnection]
  );

  const handleDisconnectOrCancel = useCallback(
    (call) => {
      updateDisconnectOrCancel(call);

      // The reason we aren't destroying in the useEffect is to disable the ability to answer call
      // even if the user didn't click "save/finished" but already hanged up and received another call
      if (deviceRef.current) {
        deviceRef.current.destroy();
      }

      setTimeout(userReloadTwilioDetails, 5000); // So the status would be updated
    },
    [userReloadTwilioDetails, updateDisconnectOrCancel]
  );

  const handleIncomingCall = useCallback(
    (call) => {
      // Set up call-specific event listeners
      call.on('accept', handleCallAccepted);
      call.on('disconnect', handleDisconnectOrCancel);
      call.on('cancel', handleDisconnectOrCancel);

      call.accept();
    },
    [handleCallAccepted, handleDisconnectOrCancel]
  );

  const handleCallFinished = useCallback(() => {
    setCallInProgress(null);
  }, [setCallInProgress]);

  const handleOffline = useCallback(() => {
    if (offlineErrorRef.current) {
      return;
    }
    offlineErrorRef.current = true;
  }, []);

  useEffect(() => {
    const handleReady = async (device) => {
      if (deviceRef.current) {
        deviceRef.current = device;
        return; // In case of connection disconnect handleReady would be called again, in the case the call would be probably already hanged up (by Twilio)
      }

      try {
        const res = await axios.post(`/api/v1/calls/twilio_accept_call/${communicationId}`, {
          call_sid: callSid,
          curr_call_sid: currCallSid,
        });
        updateCommunication(res.data);
        deviceRef.current = device;
        userReloadTwilioDetails();
      } catch (error) {
        device.destroy();
        await reportAxiosError(error);
        setErrorStartingCall(true);
        handleCallFinished(); // We want to initialize callInProgress to enable receiving new calls
      }
    };

    const initializeCallCenter = async () => {
      const token = await fetchAccessToken();
      const device = new Device(token, {
        // Disable incoming call sounds since we auto-answer
        sounds: {
          incoming: null,
        },
        tokenRefreshMs: TOKEN_REFRESH_MS,
      });

      attachTokenRefreshHandler(device, fetchAccessToken);
      device.on('registered', () => handleReady(device));
      device.on('unregistered', handleOffline);
      device.on('incoming', handleIncomingCall);
      device.on('error', function (error) {
        if (TWILIO_ERRORS_TO_IGNORE.includes(error.code)) {
          return;
        }

        reportTwilioError(error);
      });

      // Register the device to start receiving events
      await device.register();
    };

    if (callSid && !hasStartedCallSetupRef.current) {
      hasStartedCallSetupRef.current = true;
      initializeCallCenter();
    }
  }, [
    callSid,
    currCallSid,
    communicationId,
    userReloadTwilioDetails,
    handleCallAccepted,
    handleDisconnectOrCancel,
    handleCallFinished,
    handleOffline,
    handleIncomingCall,
    updateCommunication,
  ]);

  useEffect(() => {
    async function updateClaimCommunication() {
      try {
        const res = await axios.get(`/api/v1/claims/${claimToAttachId}/communications/${communicationId}`);
        updateCommunication(res.data);
      } catch (error) {
        await reportAxiosError(error);
      }
    }

    if (!claimToAttachId) {
      return;
    }

    updateClaimCommunication();
  }, [claimToAttachId, communicationId, updateCommunication]);

  if (errorStartingCall) {
    return <></>;
  }

  if (callStatus === 'pending' || isEmpty(communication) || isNil(communication) || isLoadingClaim) {
    return (
      <LoadingDialog
        isError={isErrorLoadingClaim}
        track="Initializing incoming phone call"
        text="Initializing Phone Call"
      />
    );
  }

  const callInProgressCardComponent = (
    <CallInProgressCard isIncoming onCallHandledAndFinished={handleCallFinished} shouldShowClive={true} />
  );

  return claimToAttachId && claim ? (
    <ClaimContextProvider claim={claim} refreshData={reloadClaim} createMinimizedContext={false}>
      {callInProgressCardComponent}
    </ClaimContextProvider>
  ) : (
    callInProgressCardComponent
  );
};

export default IncomingCallInProgressContainer;
